export const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const validatePdfFile = (file) => {
  const extension = String(file?.name || '').split('.').pop()?.toLowerCase();
  return Boolean(file && ['pdf', 'doc', 'docx'].includes(extension));
};

export const validateApiKey = (key) => {
  return key && key.trim().length > 0;
};

export const MAX_MANUAL_INPUT_IMAGES = 10;
export const MAX_MANUAL_INPUT_IMAGE_BYTES = 10 * 1024 * 1024;

const MANUAL_INPUT_IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
]);

const MANUAL_REQUIREMENT_STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'by',
  'for',
  'from',
  'in',
  'is',
  'it',
  'of',
  'on',
  'or',
  'the',
  'to',
  'with',
]);

const MANUAL_REQUIREMENT_SIGNAL_WORDS = new Set([
  'accept',
  'access',
  'action',
  'admin',
  'allow',
  'approve',
  'assign',
  'authenticate',
  'button',
  'cancel',
  'case',
  'change',
  'check',
  'condition',
  'create',
  'delete',
  'display',
  'download',
  'edit',
  'enter',
  'error',
  'expected',
  'export',
  'field',
  'filter',
  'flow',
  'form',
  'generate',
  'given',
  'if',
  'import',
  'input',
  'invalid',
  'journey',
  'login',
  'logout',
  'mandatory',
  'message',
  'module',
  'must',
  'navigate',
  'optional',
  'page',
  'permission',
  'reject',
  'required',
  'requirement',
  'result',
  'role',
  'rule',
  'save',
  'scenario',
  'screen',
  'search',
  'select',
  'should',
  'show',
  'state',
  'status',
  'step',
  'submit',
  'success',
  'system',
  'test',
  'then',
  'update',
  'upload',
  'user',
  'validate',
  'validation',
  'verify',
  'view',
  'when',
  'workflow',
]);

const MANUAL_REQUIREMENT_SIGNAL_PHRASES = [
  'acceptance criteria',
  'business rule',
  'expected result',
  'test case',
  'user story',
];

const normalizeManualText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const extractManualRequirementWords = (value = '') => {
  const matches = normalizeManualText(value).match(/[\p{L}][\p{L}\p{N}'-]*/gu);
  return (matches || []).map((word) => word.replace(/^'+|'+$/g, '').toLowerCase()).filter(Boolean);
};

const getManualRequirementSignal = (normalizedText, words) => {
  const wordSet = new Set(words);
  if (Array.from(MANUAL_REQUIREMENT_SIGNAL_WORDS).some((word) => wordSet.has(word))) {
    return true;
  }
  const lowered = String(normalizedText || '').toLowerCase();
  return MANUAL_REQUIREMENT_SIGNAL_PHRASES.some((phrase) => lowered.includes(phrase));
};

export const validateManualRequirementInput = (text = '', title = '') => {
  const normalizedText = normalizeManualText(text);
  const normalizedTitle = normalizeManualText(title);

  if (normalizedTitle && !/\p{L}/u.test(normalizedTitle)) {
    return {
      valid: false,
      message: 'Use a short feature name with words, not only numbers or symbols.',
    };
  }

  if (normalizedText.length < 20) {
    return {
      valid: false,
      message: 'Add requirement details before creating test cases.',
    };
  }

  const visibleChars = Array.from(normalizedText).filter((char) => !/\s/u.test(char));
  const alphaChars = visibleChars.filter((char) => /\p{L}/u.test(char));
  const digitChars = visibleChars.filter((char) => /\p{N}/u.test(char));
  const alnumChars = visibleChars.filter((char) => /[\p{L}\p{N}]/u.test(char));
  const alphaRatio = alphaChars.length / Math.max(1, visibleChars.length);
  const digitRatio = digitChars.length / Math.max(1, alnumChars.length);

  if (alphaChars.length < 20 || alphaRatio < 0.35 || digitRatio > 0.65) {
    return {
      valid: false,
      message: 'Requirement text should describe the feature in words, not only numbers or symbols.',
    };
  }

  const words = extractManualRequirementWords(normalizedText);
  const meaningfulWords = words.filter((word) => word.length > 1 && !MANUAL_REQUIREMENT_STOP_WORDS.has(word));
  const uniqueWords = new Set(meaningfulWords);

  if (words.length < 6 || meaningfulWords.length < 4) {
    return {
      valid: false,
      message: 'Add a little more detail, like the user action, rule, validation, or expected result.',
    };
  }

  if (uniqueWords.size < 4) {
    return {
      valid: false,
      message: 'The text repeats too much. Add clear requirement details before continuing.',
    };
  }

  const counts = meaningfulWords.reduce((acc, word) => {
    acc[word] = (acc[word] || 0) + 1;
    return acc;
  }, {});
  const highestRepeat = Math.max(0, ...Object.values(counts));
  if (highestRepeat >= 4 && highestRepeat / Math.max(1, meaningfulWords.length) > 0.5) {
    return {
      valid: false,
      message: 'The text repeats too much. Add clear requirement details before continuing.',
    };
  }

  if (/([^\s])\1{9,}/u.test(normalizedText)) {
    return {
      valid: false,
      message: 'This does not look like requirement content yet. Add the behavior you want to test.',
    };
  }

  if (!getManualRequirementSignal(normalizedText, meaningfulWords)) {
    return {
      valid: false,
      message: 'This does not look like requirement content yet. Add a user action, business rule, validation, or expected result.',
    };
  }

  return {
    valid: true,
    message: '',
  };
};

export const validateManualInputImages = (files = []) => {
  const selectedFiles = Array.from(files || []);
  if (!selectedFiles.length) {
    return {
      valid: true,
      files: [],
      message: '',
    };
  }

  const supportedFiles = selectedFiles.filter((file) => {
    const type = String(file?.type || '').toLowerCase();
    return (
      MANUAL_INPUT_IMAGE_TYPES.has(type) &&
      Number(file?.size || 0) > 0 &&
      Number(file?.size || 0) <= MAX_MANUAL_INPUT_IMAGE_BYTES
    );
  });

  const hasUnsupportedType = selectedFiles.some((file) => {
    const type = String(file?.type || '').toLowerCase();
    return !MANUAL_INPUT_IMAGE_TYPES.has(type);
  });
  const hasEmptyFile = selectedFiles.some((file) => Number(file?.size || 0) <= 0);
  const hasLargeFile = selectedFiles.some((file) => Number(file?.size || 0) > MAX_MANUAL_INPUT_IMAGE_BYTES);
  const limitedFiles = supportedFiles.slice(0, MAX_MANUAL_INPUT_IMAGES);
  const hasTooManyFiles = supportedFiles.length > MAX_MANUAL_INPUT_IMAGES || selectedFiles.length > MAX_MANUAL_INPUT_IMAGES;

  let message = '';
  if (hasUnsupportedType) {
    message = 'Some images could not be added. Use PNG, JPG, or WebP images.';
  } else if (hasLargeFile) {
    message = 'Some images are too large. Each image should be 10 MB or less.';
  } else if (hasEmptyFile) {
    message = 'One selected image is empty. Please choose another image.';
  } else if (hasTooManyFiles) {
    message = `You can attach up to ${MAX_MANUAL_INPUT_IMAGES} images. I kept the first ${MAX_MANUAL_INPUT_IMAGES}.`;
  }

  return {
    valid: !message,
    files: limitedFiles,
    message,
  };
};
